import { Clock, Package, Boxes, Route, Target, CheckCircle2 } from 'lucide-react';
import { formatDuration } from '@/utils/format';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { EmptyState } from '@/components/shared/EmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { ReferenceComparisonAggregation } from '@/features/analytics/types';

type MetricsSectionProps = {
  aggregation: ReferenceComparisonAggregation;
  isLoading: boolean;
};

type MetricRow = {
  id: string;
  label: string;
  icon: React.ReactNode;
  values: (number | null)[];
  format: (value: number | null) => string;
  helper?: string;
};

export function MetricsSection({ aggregation, isLoading }: MetricsSectionProps) {
  const { t, language } = useTranslation();
  const { resolve } = useReferenceAlias();
  const hasComparisonItems = aggregation.items.length > 0;

  if (isLoading) {
    return (
      <section className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </section>
    );
  }

  if (!hasComparisonItems) {
    return (
      <EmptyState
        size="compact"
        title={t('analytics.comparison.layout.state.emptyShort')}
        className="min-h-[120px]"
      />
    );
  }

  // Format helpers
  const formatNumber = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString(language);
  };

  const formatVolume = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString(language)} cm³`;
  };

  const formatDistance = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString(language)} mm`;
  };

  const formatMalround = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(3)} mm`;
  };

  // Build metric rows
  const metricRows: MetricRow[] = [
    {
      id: 'duration',
      label: t('analytics.comparison.layout.table.totalDuration'),
      icon: <Clock className="h-4 w-4" />,
      values: aggregation.items.map((item) => item.averageCycleSeconds),
      format: formatDuration,
      helper: t('analytics.comparison.layout.table.totalDurationHelper'),
    },
    {
      id: 'parts',
      label: t('analytics.comparison.layout.table.numberOfParts'),
      icon: <Package className="h-4 w-4" />,
      values: aggregation.items.map((item) => item.totalPieces),
      format: formatNumber,
      helper: t('analytics.comparison.layout.table.numberOfPartsHelper'),
    },
    {
      id: 'volume',
      label: t('analytics.comparison.layout.table.volumeRemoved'),
      icon: <Boxes className="h-4 w-4" />,
      values: aggregation.items.map((item) => item.totalVolume),
      format: formatVolume,
      helper: t('analytics.comparison.layout.table.volumeRemovedHelper'),
    },
    {
      id: 'distance',
      label: t('analytics.comparison.layout.table.distanceTravelled'),
      icon: <Route className="h-4 w-4" />,
      values: aggregation.items.map((item) => item.totalDistance),
      format: formatDistance,
      helper: t('analytics.comparison.layout.table.distanceTravelledHelper'),
    },
    {
      id: 'malround',
      label: t('analytics.comparison.layout.table.malround'),
      icon: <Target className="h-4 w-4" />,
      values: aggregation.items.map((item) => item.malroundAverage),
      format: formatMalround,
      helper: t('analytics.comparison.layout.table.malroundHelper'),
    },
  ];

  // Determine if we have a baseline (first reference)
  const hasBaseline = aggregation.items.length > 0;
  const references = aggregation.items.map((item) => item.reference);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="section-title">
          {t('analytics.comparison.layout.table.title')}
        </h2>
        <span className="text-sm text-muted-foreground">
          {t('analytics.comparison.layout.table.subtitle', {
            count: aggregation.items.length,
          })}
        </span>
      </div>

      <Card padding="none" className="overflow-hidden">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Header */}
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="sticky left-0 z-10 bg-muted/95 px-6 py-4 text-left backdrop-blur-sm">
                  <span className="eyebrow">
                    {t('analytics.comparison.layout.table.metric')}
                  </span>
                </th>
                {references.map((reference, index) => (
                  <th key={reference} className="min-w-[160px] px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className="text-sm font-semibold text-foreground">{resolve(reference).label}</span>
                      {index === 0 && hasBaseline && (
                        <Badge tone="primary">
                          <CheckCircle2 className="h-3 w-3" aria-hidden />
                          {t('analytics.comparison.layout.table.baseline')}
                        </Badge>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {metricRows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  className={`border-b border-border/50 transition-colors hover:bg-muted/40 ${
                    rowIndex === metricRows.length - 1 ? 'border-b-0' : ''
                  }`}
                >
                  {/* Metric Label */}
                  <td className="sticky left-0 z-10 bg-card px-6 py-4 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        {row.icon}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{row.label}</span>
                        {row.helper && (
                          <span className="text-xs text-muted-foreground">{row.helper}</span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Values */}
                  {row.values.map((value, colIndex) => {
                    const isBaseline = colIndex === 0 && hasBaseline;
                    const formattedValue = row.format(value);

                    return (
                      <td
                        key={colIndex}
                        className={`px-6 py-4 text-center ${
                          isBaseline ? 'bg-primary/5 dark:bg-primary/10' : ''
                        }`}
                      >
                        <span
                          className={`text-sm font-semibold ${
                            isBaseline ? 'text-primary' : 'text-foreground'
                          }`}
                        >
                          {formattedValue}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
